import type { DragEvent } from "react";

import type { Template } from "@/api";
import { Badge } from "@/components/ui/badge";
import { LinkChips } from "@/components/link-chips";
import {
  TEMPLATE_DRAG_TYPE,
  type TemplateDragPayload,
} from "@/components/timeline";
import { formatMinutes } from "@/lib/color";
import { typeLabel } from "@/lib/domain";
import { cn } from "@/lib/utils";

/**
 * A Template as a draggable card in a Label column. Dragging carries the
 * Template and the column's Label (which colours the scheduled block).
 */
export function DraggableTemplateCard({
  template,
  labelId,
  accent,
  disabled,
  title,
  onDoubleClick,
}: {
  template: Template;
  labelId: string;
  accent?: string;
  disabled?: boolean;
  title?: string;
  onDoubleClick?: () => void;
}) {
  function handleDragStart(ev: DragEvent<HTMLDivElement>) {
    const payload: TemplateDragPayload = {
      template_id: template.id,
      label_id: labelId,
    };
    ev.dataTransfer.setData(TEMPLATE_DRAG_TYPE, JSON.stringify(payload));
    ev.dataTransfer.setData("text/plain", template.title);
    ev.dataTransfer.effectAllowed = "copy";
  }
  return (
    <div
      draggable={!disabled}
      onDragStart={handleDragStart}
      onDoubleClick={disabled ? undefined : onDoubleClick}
      title={title}
      className={cn(
        "cursor-grab rounded-lg border border-border bg-card p-3 shadow-sm transition-colors select-none hover:bg-secondary/60 active:cursor-grabbing",
        disabled && "opacity-60",
      )}
      style={
        accent ? { borderLeftWidth: 4, borderLeftColor: accent } : undefined
      }
    >
      <div className="flex items-start gap-2">
        <span className="min-w-0 flex-1 truncate text-sm font-medium">
          {template.title}
        </span>
        {template.is_priority && <Badge>Priority</Badge>}
      </div>
      <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
        <Badge variant="outline">{typeLabel(template.type)}</Badge>
        <span>
          {formatMinutes(template.duration_minutes)}
          {template.type === "project" ? " left" : ""}
        </span>
        {template.suggested_start && <span>at {template.suggested_start}</span>}
      </div>
      <LinkChips links={template.links} className="mt-1.5" />
    </div>
  );
}
